import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PackageSearch } from "lucide-react";

const roleHome = (role) => ({ merchant: "/merchant", courier: "/courier", admin: "/admin", customer: "/app" }[role] || "/app");

function GoogleButton() {
  const { t } = useTranslation();
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button data-testid="google-login-btn" type="button" onClick={onClick} variant="outline" className="w-full h-11 rounded-xl gap-2">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" />
      {t("auth.continueGoogle")}
    </Button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const { setLang } = useLang();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.language) setLang(u.language);
      toast.success(t("auth.welcome"));
      navigate(roleHome(u.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const fill = (em) => { setEmail(em); setPassword("Demo123!"); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-slate-950 text-white relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand/20 rounded-full blur-[120px]" />
        <Link to="/" className="relative flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><PackageSearch className="w-5 h-5 text-white" /></div>
          <span className="font-display font-extrabold text-lg">Drivons<span className="text-brand">Market</span></span>
        </Link>
        <div className="relative">
          <h2 className="text-4xl font-display font-black leading-tight">{t("landing.title1")}<br />{t("landing.title2")}<br /><span className="text-brand">{t("landing.title3")}</span></h2>
          <p className="text-slate-400 mt-4 max-w-sm">Local stores. Smart delivery. Right vehicle, every time.</p>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">ISRAEL · TURKEY · UAE</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex justify-end mb-2"><LanguageSwitcher /></div>
          <h1 className="text-2xl font-display font-bold">{t("auth.loginTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("auth.welcome")}</p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <Label>{t("auth.email")}</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11 rounded-xl" placeholder="you@example.com" />
            </div>
            <div>
              <Label>{t("auth.password")}</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-11 rounded-xl" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" disabled={busy} className="w-full h-11 rounded-xl bg-brand hover:bg-brand-dark">{busy ? "…" : t("auth.loginTitle")}</Button>
          </form>

          <div className="flex items-center gap-3 my-5"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">{t("auth.or")}</span><div className="h-px bg-border flex-1" /></div>
          <GoogleButton />

          <p className="text-sm text-center mt-6 text-muted-foreground">{t("auth.noAccount")} <Link to="/register" className="text-brand font-semibold">{t("auth.signup")}</Link></p>

          <div className="mt-6 rounded-xl border bg-muted/40 p-3 text-xs space-y-1">
            <div className="font-semibold text-muted-foreground">{t("auth.demoLogins")}</div>
            <div className="flex flex-wrap gap-1.5">
              {[["Customer", "customer@drivons.market"], ["Merchant", "buildpro@drivons.market"], ["Courier", "courier@drivons.market"]].map(([l, em]) => (
                <button key={em} type="button" data-testid={`demo-${l.toLowerCase()}`} onClick={() => fill(em)} className="px-2 py-1 rounded-md bg-card border hover:border-brand">{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
